// src/utils/patchMap.ts
export const FIELD_MAP: Record<string, string> = {
  name: 'name', description: 'description', selectedCoins: 'selected_coins', quoteCurrency: 'quote_currency',
  primaryTimeInterval: 'primary_time_interval', primaryTimeIndicators: 'primary_time_indicators',
  secondaryTimeEnabled: 'secondary_time_enabled', secondaryTimeInterval: 'secondary_time_interval', secondaryTimeIndicators: 'secondary_time_indicators',
  exchange: 'exchange', strategyKey: 'strategy_key', deploymentTarget: 'deployment_target', origin: 'origin',

  longEnabled: 'long_entry_enabled', longEntryEnabled: 'long_entry_enabled', 
  shortEnabled: 'short_entry_enabled', shortEntryEnabled: 'short_entry_enabled',

  buyOrderType: 'buy_order_type', longOrderType: 'buy_order_type', orderType: 'buy_order_type',
  maxOpenPositions: 'max_open_positions', onlyOneOpenBuyOrder: 'only_one_open_buy_order', onlyOneOpenOrder: 'only_one_open_buy_order',
  onlyPositivePairs: 'only_positive_pairs', buyPercentRange: 'buy_percent_range', percentRange: 'buy_percent_range', buyCooldownTime: 'buy_cooldown_time', cooldownTime: 'buy_cooldown_time',

  sellOrderType: 'sell_order_type', shortOrderType: 'sell_order_type', maxOpenOrders: 'max_open_orders', onlyOneOpenSellOrder: 'only_one_open_sell_order',
  onlyNegativePairs: 'only_negative_pairs', sellPercentRange: 'sell_percent_range', sellCooldownTime: 'sell_cooldown_time',

  longDcaEnabled: 'long_dca_enabled', longDcaStages: 'long_dca_stages', buyTakeProfitUseFixedAmount: 'buy_take_profit_use_fixed_amount',
  shortDcaEnabled: 'short_dca_enabled', shortDcaStages: 'short_dca_stages', sellTakeProfitUseFixedAmount: 'sell_take_profit_use_fixed_amount',
  
  buyTrailingEntryEnabled: 'buy_trailing_entry_enabled', buyTrailingEntryUseFixedAmount: 'buy_trailing_entry_use_fixed_amount', buyTrailingEntryStages: 'buy_trailing_entry_stages',
  sellTrailingExitEnabled: 'sell_trailing_exit_enabled', sellTrailingExitUseFixedAmount: 'sell_trailing_exit_use_fixed_amount', sellTrailingExitStages: 'sell_trailing_exit_stages',
  
  buyTakeProfitStagesEnabled: 'buy_take_profit_stages_enabled', buyTakeProfitStagesUseFixedAmount: 'buy_take_profit_stages_use_fixed_amount',
  buyTakeProfitStagesInitialTrailingStop: 'buy_take_profit_stages_initial_trailing_stop', buyTakeProfitStagesDynamicTrailingEnabled: 'buy_take_profit_stages_dynamic_trailing_enabled',
  buyTakeProfitStagesAutoAdjustEnabled: 'buy_take_profit_stages_auto_adjust_enabled', buyTakeProfitStagesData: 'buy_take_profit_stages_data',

  sellTakeProfitStagesEnabled: 'sell_take_profit_stages_enabled', sellTakeProfitStagesUseFixedAmount: 'sell_take_profit_stages_use_fixed_amount',
  sellTakeProfitStagesInitialTrailingStop: 'sell_take_profit_stages_initial_trailing_stop', sellTakeProfitStagesDynamicTrailingEnabled: 'sell_take_profit_stages_dynamic_trailing_enabled',
  sellTakeProfitStagesAutoAdjustEnabled: 'sell_take_profit_stages_auto_adjust_enabled', sellTakeProfitStagesData: 'sell_take_profit_stages_data',

  accountBalance: 'account_balance', percentageAmount: 'percentage_amount', minAmount: 'min_amount', useFixedAmount: 'use_fixed_amount', maxAllocation: 'max_allocation',
  timeBasedEnabled: 'time_based_enabled', maxDailyTrades: 'max_daily_trades', precisionPercentage: 'precision_percentage', exchangeFeePercentage: 'exchange_fee_percentage'
};

const JSON_ARRAY_COLS = ['long_dca_stages', 'short_dca_stages', 'buy_trailing_entry_stages', 'sell_trailing_exit_stages', 'buy_take_profit_stages_data', 'sell_take_profit_stages_data', 'primary_time_indicators', 'secondary_time_indicators', 'selected_coins'];
const NUMERIC_COLS = ['max_open_positions', 'buy_percent_range', 'buy_cooldown_time', 'max_open_orders', 'sell_percent_range', 'sell_cooldown_time', 'buy_take_profit_stages_initial_trailing_stop', 'sell_take_profit_stages_initial_trailing_stop', 'percentage_amount', 'account_balance', 'min_amount', 'max_allocation', 'max_daily_trades', 'precision_percentage', 'exchange_fee_percentage'];

export const sanitizeValue = (val: any, dbColumn: string = '') => {
  if (val === '' || val === null || val === undefined) return null; 
  if (typeof val === 'string' && NUMERIC_COLS.includes(dbColumn)) {
    const num = Number(val.replace(',', '.'));
    return isNaN(num) ? null : num;
  }
  return val;
};

export const sanitizeObject = (obj: any): any => {
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeObject);
  const cleaned: any = {};
  for (const [k, v] of Object.entries(obj)) cleaned[k] = sanitizeValue(v, k);
  return cleaned;
};

export function buildPatchFromChange(field: string, value: unknown) {
  let col = FIELD_MAP[field] || field;
  if (JSON_ARRAY_COLS.includes(col)) {
    let finalValue = value;
    if (typeof finalValue === 'string') { try { finalValue = JSON.parse(finalValue); } catch { finalValue = []; } }
    if (Array.isArray(finalValue)) return { [col]: finalValue.map(v => sanitizeObject(v)) };
    return { [col]: [] };
  }
  return { [col]: sanitizeValue(value, col) };
}

export function mapFormToDb(formData: Record<string, any>): Record<string, any> {
  const dbData: Record<string, any> = { settings: {} };
  Object.keys(formData).forEach(key => {
    if (key === 'id') return;
    const dbColumn = FIELD_MAP[key];
    if (dbColumn) {
      let val = formData[key];
      if (JSON_ARRAY_COLS.includes(dbColumn)) {
        if (typeof val === 'string') { try { val = JSON.parse(val); } catch { val = []; } }
        dbData[dbColumn] = Array.isArray(val) ? val.map((v: any) => sanitizeObject(v)) : [];
      } else {
        dbData[dbColumn] = sanitizeValue(val, dbColumn);
      }
    } else {
      dbData.settings[key] = sanitizeValue(formData[key], key);
    }
  });
  return dbData;
}

export function mapDbToForm(dbData: Record<string, any>): Record<string, any> {
  const formData: Record<string, any> = {};
  const reverseMap: Record<string, string> = {};
  Object.entries(FIELD_MAP).forEach(([camelKey, snakeKey]) => { if (!reverseMap[snakeKey]) reverseMap[snakeKey] = camelKey; });
  
  let settingsJson = dbData['settings_json'];
  if (typeof settingsJson === 'string') { try { settingsJson = JSON.parse(settingsJson); } catch(e) {} }
  if (settingsJson && typeof settingsJson === 'object') Object.assign(formData, settingsJson);

  let settingsObj = dbData['settings'];
  if (typeof settingsObj === 'string') { try { settingsObj = JSON.parse(settingsObj); } catch(e) {} }
  if (settingsObj && typeof settingsObj === 'object') Object.assign(formData, settingsObj);
  
  Object.keys(dbData).forEach(key => {
    if (key === 'settings' || key === 'settings_json') return;
    
    const formField = reverseMap[key] || key;
    let val = dbData[key];
    if (typeof val === 'string' && (val.startsWith('[') || val.startsWith('{'))) { 
      try { val = JSON.parse(val); } catch (e) {} 
    }
    
    if (val !== null && val !== undefined) {
       formData[formField] = val;
    }
  });

  const parseBool = (v: any, fallback: boolean | null): boolean | null => {
      if (v === true || v === 'true' || v === 1 || v === '1') return true;
      if (v === false || v === 'false' || v === 0 || v === '0') return false;
      return fallback;
  };

  const dbLong = parseBool(dbData.long_entry_enabled, null) ?? parseBool(dbData.long_enabled, null) ?? parseBool(dbData.buy_enabled, null);
  const dbShort = parseBool(dbData.short_entry_enabled, null) ?? parseBool(dbData.short_enabled, null) ?? parseBool(dbData.sell_enabled, null);

  formData.longEnabled = dbLong !== null ? dbLong : (parseBool(formData.longEnabled, null) ?? true);
  formData.shortEnabled = dbShort !== null ? dbShort : (parseBool(formData.shortEnabled, null) ?? false);

  delete formData.long_entry_enabled;
  delete formData.long_enabled;
  delete formData.buy_enabled;
  delete formData.short_entry_enabled;
  delete formData.short_enabled;
  delete formData.sell_enabled;

  return formData;
}